import { Globe, Target, Lightbulb, ArrowRight } from 'lucide-react';
import type { TabRealWorldApplication } from '../../../../types/craft';
import FormattedText from '../../../common/FormattedText';

interface Props {
  data: TabRealWorldApplication;
}

export default function RealWorldApplicationTab({ data }: Props) {
  if (!data?.summary && !data?.scenarios?.length) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-app-muted">No data available yet.</p>
        <p className="text-xs text-app-muted mt-1">Generate this module to populate content.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {data.summary && (
        <div className="bg-app-surface rounded-lg border border-app-line p-6">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-4 h-4 text-app-focus" />
            <h4 className="text-sm font-semibold text-app-ink">Real-World Context</h4>
          </div>
          <FormattedText text={data.summary} className="text-sm text-app-ink" />
        </div>
      )}

      {data.scenarios?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-app-ink mb-4">Scenarios</h4>
          <div className="space-y-4">
            {data.scenarios.map((s, i) => (
              <div key={i} className="bg-app-surface border border-app-line rounded-lg overflow-hidden">
                <div className="px-5 py-3 bg-app-canvas border-b border-app-line">
                  <h5 className="text-sm font-semibold text-app-ink">{s.title}</h5>
                </div>
                <div className="p-5 space-y-4">
                  {s.context && (
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-app-muted">Context</span>
                      <p className="text-xs text-app-ink leading-relaxed mt-0.5">{s.context}</p>
                    </div>
                  )}
                  {s.approach && (
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-app-focus flex items-center gap-1">
                        <Target className="w-3 h-3" /> Approach
                      </span>
                      <p className="text-xs text-app-ink leading-relaxed mt-0.5">{s.approach}</p>
                    </div>
                  )}
                  {s.outcome && (
                    <div className="bg-app-positive/10 border border-app-positive rounded-lg px-3 py-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-app-positive">Outcome</span>
                      <p className="text-xs text-app-positive leading-relaxed mt-0.5">{s.outcome}</p>
                    </div>
                  )}
                  {s.lesson && (
                    <div className="flex items-start gap-2">
                      <Lightbulb className="w-3.5 h-3.5 text-app-warning mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-app-muted"><span className="font-medium">Lesson:</span> {s.lesson}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.deal_examples?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-app-ink mb-3">Deal Examples</h4>
          <div className="space-y-2">
            {data.deal_examples.map((ex, i) => (
              <div key={i} className="flex items-start gap-2 bg-app-surface border border-app-line rounded-lg p-3">
                <ArrowRight className="w-3.5 h-3.5 text-app-focus mt-0.5 flex-shrink-0" />
                <p className="text-xs text-app-ink leading-relaxed">{ex}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
